import React, { useState, useEffect } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Routes, Route, Link, useNavigate, Navigate, useLocation } from 'react-router-dom';
import ItemizationSimulation from './components/ItemizationSimulation';
import EnhancedItemizationSimulation from './components/EnhancedItemizationSimulation';
import Scenes from './components/Scenes';
import { Hero } from './types/types';
import { testFirebaseConnection } from './utils/firebaseTest';
import { DotaService } from './services/DotaService';
import { AuthProvider } from './contexts/AuthContext';
import { TimeProvider } from './contexts/TimeContext';
import './styles/App.css';

const App: React.FC = () => {
  const [radiantHeroes, setRadiantHeroes] = useState<Hero[]>([]);
  const [direHeroes, setDireHeroes] = useState<Hero[]>([]);
  const [matchId, setMatchId] = useState<string>('');
  const [searchMatchId, setSearchMatchId] = useState<string>('');
  const [sessionId, setSessionId] = useState<string>('');
  const [searchSessionId, setSearchSessionId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    console.log('🔄 App mounted - Testing Firebase connection...');
    const cleanup = testFirebaseConnection();
    
    // Log Firebase config for debugging
    console.log('Firebase Environment Variables:');
    Object.entries({
      API_KEY: import.meta.env.VITE_FIREBASE_API_KEY?.slice(0, 5) + '...',
      PROJECT_ID: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      DATABASE_URL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
    }).forEach(([key, value]) => {
      console.log(`${key}: ${value || '❌ Missing'}`);
    });

    // Check for matchId in URL parameters
    const params = new URLSearchParams(location.search);
    const matchIdParam = params.get('matchId');
    if (matchIdParam) {
      console.log(`Found matchId in URL: ${matchIdParam}`);
      setSearchMatchId(matchIdParam);
      // Auto-load the match if ID is provided in URL
      setTimeout(() => loadMatch(matchIdParam), 500);
    }

    return () => {
      cleanup();
      console.log('🧹 Firebase connection test cleaned up');
    };
  }, [location]);

  // Load session data from Firebase when only a session ID is provided
  const loadSessionData = async () => {
    if (!searchSessionId.trim()) {
      setError('Please enter a session ID');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      console.log(`Attempting to load session with ID: ${searchSessionId}`);
      
      // Fetch session data from Firebase
      const sessionData = await DotaService.getSessionData(searchSessionId);
      
      if (!sessionData) {
        console.error(`Session not found: ${searchSessionId}`);
        setError(`Session not found: "${searchSessionId}". Please check the session ID.`);
        setLoading(false);
        return;
      }
      
      console.log('Session data loaded:', sessionData);
      
      // Extract match ID and other data from the session
      const { matchId: sessionMatchId } = sessionData;
      
      if (!sessionMatchId) {
        console.error('Invalid session data: No match ID found');
        setError('Invalid session data: No match ID found');
        setLoading(false);
        return;
      }
      
      console.log(`Loading match data for session's match ID: ${sessionMatchId}`);
      
      // Now load the match data using the match ID from the session
      const match = await DotaService.getMatch(sessionMatchId);
      if (!match.players || match.players.length !== 10) {
        console.error(`Invalid match data for match ID ${sessionMatchId} - wrong player count`);
        setError('Invalid match data - wrong player count');
        setLoading(false);
        return;
      }
      
      // Load heroes
      const heroes = await DotaService.getHeroes();
      
      const radiant = match.players
        .filter(p => p.team_number === 0)
        .map(p => {
          const hero = heroes.find(h => h.id === p.hero_id);
          return hero;
        })
        .filter((hero): hero is Hero => hero !== undefined);

      const dire = match.players
        .filter(p => p.team_number === 1)
        .map(p => {
          const hero = heroes.find(h => h.id === p.hero_id);
          return hero;
        })
        .filter((hero): hero is Hero => hero !== undefined);
      
      if (radiant.length === 5 && dire.length === 5) {
        console.log(`Successfully loaded session ${searchSessionId} with match ${sessionMatchId}`);
        setRadiantHeroes(radiant);
        setDireHeroes(dire);
        setMatchId(sessionMatchId);
        setSessionId(searchSessionId);
        navigate('/itemization');
      } else {
        console.error(`Invalid team sizes - Radiant: ${radiant.length}, Dire: ${dire.length}`);
        setError(`Invalid team sizes - Radiant: ${radiant.length}, Dire: ${dire.length}`);
      }
    } catch (err) {
      console.error('Session loading error:', err);
      setError(`Failed to load session: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const loadMatch = async (specificMatchId?: string) => {
    // If only a session ID is provided, use that to load the match
    if (searchSessionId.trim() && !searchMatchId.trim() && !specificMatchId) {
      loadSessionData();
      return;
    }
    
    // Use provided specific matchId or fall back to the searchMatchId state
    const matchIdToLoad = specificMatchId || searchMatchId.trim();
    
    if (!matchIdToLoad) {
      setError('Please enter a match ID');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const match = await DotaService.getMatch(matchIdToLoad);
      if (!match.players || match.players.length !== 10) {
        setError('Invalid match data - wrong player count');
        return;
      }
      
      // Load heroes if needed
      const heroes = await DotaService.getHeroes();
      
      const radiant = match.players
        .filter(p => p.team_number === 0)
        .map(p => {
          const hero = heroes.find(h => h.id === p.hero_id);
          return hero;
        })
        .filter((hero): hero is Hero => hero !== undefined);

      const dire = match.players
        .filter(p => p.team_number === 1)
        .map(p => {
          const hero = heroes.find(h => h.id === p.hero_id);
          return hero;
        })
        .filter((hero): hero is Hero => hero !== undefined);
      
      if (radiant.length === 5 && dire.length === 5) {
        // Generate session ID first if not provided
        const newSessionId = searchSessionId.trim() || generateRandomSessionId();
        
        // Create session in Firebase if it's a new session
        if (!searchSessionId.trim()) {
          try {
            await DotaService.createSession({
              matchId: matchIdToLoad,
              createdAt: new Date().toISOString(),
              sessionId: newSessionId,
            });
            console.log(`Created new session with ID: ${newSessionId}`);
          } catch (err) {
            console.error('Error creating session:', err);
            // Continue even if session creation fails
          }
        }
        
        setRadiantHeroes(radiant);
        setDireHeroes(dire);
        setMatchId(matchIdToLoad);
        setSessionId(newSessionId);
        navigate('/itemization');
      } else {
        setError(`Invalid team sizes - Radiant: ${radiant.length}, Dire: ${dire.length}`);
      }
    } catch (err) {
      console.error('Match loading error:', err);
      setError('Failed to load match data');
    } finally {
      setLoading(false);
    }
  };

  // Generate a random session ID if none provided
  const generateRandomSessionId = () => {
    return Math.random().toString(36).substring(2, 10);
  };

  // Join a session directly with just a session ID
  const joinSession = async () => {
    if (!searchSessionId.trim()) {
      setError('Please enter a session ID');
      return;
    }
    
    loadSessionData();
  };

  const renderLandingPage = () => (
    <div className="landing-page">
      <h1 style={{ textAlign: 'center', marginTop: '20px', color: '#fff' }}>Dota 2 Itemization Simulator</h1>
      <div className="match-search" style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        padding: '50px',
        borderRadius: '8px',
        maxWidth: '600px',
        margin: '50px auto',
        backgroundColor: 'rgba(0, 0, 0, 0.7)'
      }}>
        <h2>Enter Match Details</h2>
        <div style={{ display: 'flex', width: '100%', marginTop: '20px', flexDirection: 'column', gap: '10px' }}>
          <div style={{ display: 'flex' }}>
            <input
              type="text"
              value={searchMatchId}
              onChange={(e) => setSearchMatchId(e.target.value)}
              placeholder="Enter Match ID"
              onKeyPress={(e) => e.key === 'Enter' && loadMatch()}
              style={{
                flex: 1,
                padding: '12px',
                fontSize: '16px',
                borderRadius: '4px 0 0 4px',
                border: 'none'
              }}
            />
            <button 
              onClick={() => loadMatch()}
              disabled={loading}
              style={{
                padding: '12px 24px',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '0 4px 4px 0',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '16px'
              }}
            >
              {loading ? 'Loading...' : 'Load Match'}
            </button>
          </div>

          <div style={{
            fontSize: '13px',
            color: '#aaa',
            width: '100%',
            textAlign: 'left',
            padding: '0 5px'
          }}>
            Try:{' '}
            <span
              onClick={() => { setSearchMatchId('8890179830'); loadMatch('8890179830'); }}
              style={{ color: '#4CAF50', cursor: 'pointer', textDecoration: 'underline' }}
            >
              8890179830
            </span>
          </div>

          <div style={{
            fontSize: '14px',
            color: '#ddd',
            width: '100%',
            textAlign: 'center',
            padding: '10px 0',
            marginTop: '5px'
          }}>
            OR
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <input
              type="text"
              value={searchSessionId}
              onChange={(e) => setSearchSessionId(e.target.value)}
              placeholder="Enter Session ID"
              onKeyPress={(e) => e.key === 'Enter' && joinSession()}
              style={{
                flex: 1,
                padding: '12px',
                fontSize: '16px',
                borderRadius: '4px 0 0 4px',
                border: 'none'
              }}
            />
            <button
              onClick={joinSession}
              disabled={loading}
              style={{
                padding: '12px 24px',
                backgroundColor: '#2980b9',
                color: 'white',
                border: 'none',
                borderRadius: '0 4px 4px 0',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '16px'
              }}
            >
              {loading ? 'Joining...' : 'Join Session'}
            </button>
          </div>
          <div style={{ 
            fontSize: '12px', 
            color: '#aaa',
            width: '100%',
            textAlign: 'left',
            padding: '0 5px'
          }}>
            Create a new session with Match ID, or join an existing session with Session ID
          </div>
        </div>
        {error && (
          <div style={{
            color: '#f44336',
            marginTop: '15px',
            padding: '10px',
            backgroundColor: 'rgba(244, 67, 54, 0.1)',
            borderRadius: '4px',
            width: '100%'
          }}>
            {error}
          </div>
        )}
        <div style={{ marginTop: '30px', color: '#aaa', textAlign: 'center' }}>
          <p>Example match ID: 7213718951</p>
        </div>
      </div>
    </div>
  );

  return (
    <AuthProvider>
    <DndProvider backend={HTML5Backend}>
      <div className="app">
          {/* Removed nav bar: {matchId && renderNavBar()} */}
          
          <Routes>
            <Route path="/" element={!matchId ? renderLandingPage() : <Navigate to="/itemization" />} />
            <Route 
              path="/itemization" 
              element={
                matchId ? (
                  <TimeProvider sessionId={sessionId}>
                    <EnhancedItemizationSimulation
            radiantHeroes={radiantHeroes}
            direHeroes={direHeroes}
            matchId={matchId}
                      sessionId={sessionId}
                    />
                  </TimeProvider>
                ) : <Navigate to="/" />
              } 
            />
            <Route 
              path="/scenes" 
              element={
                matchId ? (
                  <TimeProvider sessionId={sessionId}>
                    <Scenes />
                  </TimeProvider>
                ) : <Navigate to="/" />
              } 
            />
          </Routes>
      </div>
    </DndProvider>
    </AuthProvider>
  );
}

export default App;
